import { clamp, denormalizeValue, normalizeValue } from "@/utils/math"
import { usePointerDrag } from "@/utils/usePointerDrag"
import clsx from "clsx"
import { useState, useEffect } from "react"
import { isDeepEqual } from "@/utils/object"
import { useTranslation } from "react-i18next"

export const DualSlider = (props: {
	title: string
	disabled?: boolean
	background: string
	value: [number, number]
	validRange: [number, number]
	resetValue?: [number, number]
	minDistance?: number
	onUpdate: (value) => void
	onUpdateEnded: (displayValue) => void
}) => {
	const { t } = useTranslation("components")
	const normalize = normalizeValue(props.value, props.validRange) as [number, number]
	const [range, setRange] = useState(normalize)
	const [isHovering, setHovering] = useState(false)
	const [isDragging, setDragging] = useState(false)

	const [startRange, setStartRange] = useState(normalize)
	// drag index: 0 for start, 1 for end, 2 for between, -1 for none
	const [dragIndex, setDragIndex] = useState(-1)
	const [startMiddlePercent, setStartMiddlePercent] = useState(0.5)
	const minDistance = props.minDistance ?? 0.1

	useEffect(() => {
		setRange(normalize)
	}, [props.value])

	const bindHandleDrag = usePointerDrag<HTMLDivElement>({
		onPointerDown(e) {
			e.stopPropagation()
			e.preventDefault()

			setStartRange([...normalize])
		},
		onPointerUp() {
			setDragging(false)
			setDragIndex(-1)
			handleCommitUpdate()
		},
		onDrag(drag) {
			setDragging(true)
			const range = normalize

			const rect = drag.currentTarget.parentElement?.getBoundingClientRect()
			const x = drag.x - (rect?.left ?? 0)
			const width = rect?.width ?? 1
			const percent = x / width
			const threshold = 0.05

			if (dragIndex === -1) {
				if (Math.abs(percent - range[0]) < threshold) {
					setDragIndex(0) // start
				} else if (Math.abs(percent - range[1]) < threshold) {
					setDragIndex(1) // end
				} else if (percent >= range[0] + threshold && percent <= range[1] - threshold) {
					setDragIndex(2) // between
					setStartMiddlePercent(percent)
				}
			}

			const distance = startRange[1] - startRange[0]
			if (dragIndex === 0) {
				const value = clamp(percent, 0, 1 - minDistance)
				const beyondDistance = value + minDistance
				let end = range[1]
				if (beyondDistance > range[1]) {
					end = beyondDistance
				}
				setRange([value, end])
				range[0] = value
				range[1] = end
			} else if (dragIndex === 1) {
				const value = clamp(percent, 0 + minDistance, 1)
				const beyondDistance = value - minDistance
				let start = range[0]
				if (beyondDistance < range[0]) {
					start = beyondDistance
				}
				setRange([start, value])
				range[0] = start
				range[1] = value
			} else if (dragIndex === 2) {
				const offset = percent - startMiddlePercent
				let start = startRange[1] + offset - distance
				let end = startRange[0] + offset + distance
				if (startRange[1] + offset - distance < 0) {
					start = 0
				}
				if (startRange[0] + offset + distance > 1) {
					end = 1
				}
				start = clamp(start, 0, 1 - minDistance)
				end = clamp(end, minDistance, 1)
				setRange([start, end])
				range[0] = start
				range[1] = end
			}

			props.onUpdate(denormalizeValue(range, props.validRange))
		},
	})

	const handleCommitUpdate = () => {
		const range = normalize

		if (!isDeepEqual(range, startRange)) {
			props.onUpdateEnded(formatRangeDisplayValue(range))
		}
	}

	const handlePointerEnter = () => {
		if (props.disabled === true) {
			return
		}

		setHovering(true)
	}

	const handlePointerLeave = () => {
		if (props.disabled === true) {
			return
		}

		setHovering(false)
	}

	const handleReset = () => {
		if (props.disabled === true) {
			return
		}

		if (props.resetValue) {
			const range = normalizeValue(props.resetValue, props.validRange) as [number, number]
			setRange(range)
			props.onUpdate(props.resetValue)
			props.onUpdateEnded(formatRangeDisplayValue(range))
		}
	}

	/* Rendering */

	const formatRangeDisplayValue = (range: [number, number]) => {
		const value = `${Math.round(range[0] * 100)}/${Math.round(range[1] * 100)}`
		return value
	}

	const renderEdge = (name: string, left: number) => {
		let style

		if (name === "start") {
			style = { left: `${left * 100}%` }
		} else {
			style = { left: `${left * 100}%` }
		}

		return (
			<div style={style} data-handle={name} className="absolute inset-y-0 w-[2px]">
				<span
					className={clsx(
						"absolute -bottom-[6px] -left-[6px] size-4 rounded-full border-2 bg-base-500 ring-inset ring-white hover:bg-base-50 dark:-bottom-[5px] dark:-left-[5px] dark:size-3 dark:bg-base-800 dark:ring-0 dark:hover:bg-base-800",
						isHovering
							? "border-black/80 bg-base-50 dark:border-white/75 dark:bg-base-800"
							: "z-10 border-black/30 dark:border-white/50",
						isDragging ? "ring-4 dark:border-4" : "ring-2",
					)}
				></span>
			</div>
		)
	}

	const renderInteger = (value: number) => {
		return `${Math.round(value * 100)}`
	}

	const renderExternalSlider = () => {
		return <div className="absolute -inset-x-[10px] -inset-y-[14px]" {...bindHandleDrag}></div>
	}

	return (
		<div
			role="group"
			aria-label={t("dualSlider.range", { title: props.title })}
			className="flex flex-row items-center justify-between text-xs"
			onPointerLeave={handlePointerLeave}
			onPointerEnter={handlePointerEnter}
			onDoubleClick={handleReset}
		>
			<input
				type="range"
				className="sr-only"
				aria-label={t("dualSlider.start", { title: props.title })}
				min={0}
				max={100}
				value={Math.round(range[0] * 100)}
				readOnly
				tabIndex={-1}
			/>
			<input
				type="range"
				className="sr-only"
				aria-label={t("dualSlider.end", { title: props.title })}
				min={0}
				max={100}
				value={Math.round(range[1] * 100)}
				readOnly
				tabIndex={-1}
			/>
			<div
				className={clsx(
					"flex w-[60px] shrink-0 flex-row items-center justify-end whitespace-nowrap",
					isHovering === true ? "text-base-50" : "opacity-50",
				)}
				onDoubleClick={handleReset}
			>
				{props.title}
			</div>
			<div className="relative ml-2 h-0.5 w-full">
				{renderEdge("start", range[0])}
				{renderEdge("end", range[1])}
				<div
					style={{ background: props.background }}
					className={clsx("h-0.5 w-full", !isHovering && "dark:brightness-50")}
				>
					{renderExternalSlider()}
				</div>
			</div>
			<div
				className={clsx(
					"w-[100px] overflow-hidden text-right transition-opacity",
					isHovering === true ? "text-base-50" : "opacity-50",
				)}
			>
				{renderInteger(range[0])}/{renderInteger(range[1])}
			</div>
		</div>
	)
}
